import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Table, Card, Spinner, Alert, Badge } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faArrowLeft, faInfoCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ArrowLeft, Save, Plus, Trash2, Calculator, FileText, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import { getApiUrl } from '../../../utils/ApiUtils';
import swal from 'sweetalert';
import PageTitle from '../../layouts/PageTitle';



function TemplateEditor() {
  const history = useHistory();
  const { companyId, templateId } = useParams();
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState('Income');
  const [transactions, setTransactions] = useState([{ account_id: '', debit_formula: '', credit_formula: '', is_user_defined: false }]);
  const [parameters, setParameters] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parameterValues, setParameterValues] = useState({});
  useEffect(() => {
    const initializeData = async () => {
      try {
        setFetchLoading(true);
        await fetchAccounts();
        if (templateId !== 'new') {
          await fetchTemplateDetails(templateId);
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setFetchLoading(false);
      }
    };
    initializeData();
  }, [templateId, companyId]);
  // Update parameter values when parameters change
  useEffect(() => {
    const newValues = {};
    parameters.forEach(param => {
      // Set appropriate default sample values based on parameter type
      if (param.parameter_name) {
        switch (param.parameter_type) {
          case 'Number':
            newValues[param.parameter_name] = 1000;
            break;
          case 'Date':
            newValues[param.parameter_name] = new Date().toISOString().split('T')[0];
            break;
          case 'Text':
            newValues[param.parameter_name] = 'Sample Text';
            break;
          case 'Boolean':
            newValues[param.parameter_name] = true;
            break;
          default:
            newValues[param.parameter_name] = 1000;
        }
      }
    });
    setParameterValues(prev => ({
      ...prev,
      ...newValues
    }));
  }, [parameters]);
  const fetchAccounts = async () => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true });
      const options = result.data.accounts.map(account => ({
        value: account.account_id,
        label: `${account.account_number} - ${account.account_name}`,
        account_number: account.account_number,
        account_name: account.account_name
      }));
      setAccounts(options);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      throw error;
    }
  };
  const fetchTemplateDetails = async (id) => {
    try {
      const response = await axios.get(getApiUrl(`${companyId}/templates/${id}`), { withCredentials: true });
      const template = response.data.template;
      setTemplateName(template.template_name || '');
      setDescription(template.description || '');
      setTemplateType(template.template_type || 'Income');
      // Ensure transactions has at least one entry
      if (template.transactions && template.transactions.length > 0) {
        setTransactions(template.transactions);
      } else {
        setTransactions([{ account_id: '', debit_formula: '', credit_formula: '', is_user_defined: false }]);
      }
      // Set parameters if they exist
      if (template.parameters && template.parameters.length > 0) {
        setParameters(template.parameters);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      throw error;
    }
  };
  const handleTransactionChange = (index, field, value) => {
    const newTransactions = [...transactions];
    newTransactions[index][field] = value;
    setTransactions(newTransactions);
  };
  const handleAddTransaction = () => {
    setTransactions([...transactions, { account_id: '', debit_formula: '', credit_formula: '', is_user_defined: false }]);
  };
  const handleRemoveTransaction = (index) => {
    if (transactions.length > 1) {
      setTransactions(transactions.filter((_, i) => i !== index));
    } else {
      swal('Cannot Remove', 'You need at least one transaction in the template.', 'warning');
    }
  };
  const handleAddParameter = () => {
    setParameters([...parameters, { parameter_name: '', parameter_label: '', parameter_type: 'Number', required: true }]);
  };
  const handleParameterChange = (index, field, value) => {
    const newParameters = [...parameters];
    newParameters[index][field] = value;
    // If changing parameter_name, update it to be a valid variable name
    if (field === 'parameter_name') {
      const validName = value
        .toLowerCase()
        .replace(/[^a-z0-9_]/gi, '_')
        .replace(/^[0-9]/, '_');
      newParameters[index].parameter_name = validName;
    }
    setParameters(newParameters);
  };
  const handleRemoveParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };
  const handleParameterValueChange = (paramName, value) => {
    setParameterValues({
      ...parameterValues,
      [paramName]: value
    });
  };
  const evaluateFormula = (formula) => {
    if (!formula) return 0;
    try {
      // Replace parameters with their values
      let evaluatableFormula = formula;
      parameters.forEach(param => {
        const paramName = param.parameter_name;
        if (paramName) {
          const regex = new RegExp(paramName, 'g');
          evaluatableFormula = evaluatableFormula.replace(regex, parameterValues[paramName] || 0);
        }
      });
      // Evaluate the formula
      // eslint-disable-next-line no-eval
      const result = eval(evaluatableFormula);
      return isNaN(result) ? 0 : result;
    } catch (error) {
      console.error('Error evaluating formula:', error);
      return 0;
    }
  };
  const validateTemplate = () => {
    // Basic validation
    if (!templateName.trim()) {
      swal("Validation Error", "Template Name is required!", "error");
      return false;
    }
    // Validate transactions
    const invalidTransactions = transactions.filter(
      t => !t.account_id || (!t.debit_formula && !t.credit_formula)
    );
    if (invalidTransactions.length > 0) {
      swal("Validation Error", "All transactions must have an account and at least one formula (debit or credit).", "error");
      return false;
    }
    // Validate parameters have names and labels
    const invalidParameters = parameters.filter(
      p => !p.parameter_name || !p.parameter_label
    );
    if (invalidParameters.length > 0) {
      swal("Validation Error", "All parameters must have both a name and label.", "error");
      return false;
    }
    // Check for duplicate parameter names
    const paramNames = parameters.map(p => p.parameter_name);
    if (new Set(paramNames).size !== paramNames.length) {
      swal("Validation Error", "Parameter names must be unique.", "error");
      return false;
    }
    return true;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateTemplate()) {
      return;
    }
    const payload = {
      template_name: templateName,
      description,
      template_type: templateType,
      transactions,
      parameters
    };
    try {
      setIsLoading(true);
      setError(null);
      if (templateId === 'new') {
        await axios.post(getApiUrl(`${companyId}/templates`), payload, { withCredentials: true });
        //swal("Success", "Template created successfully!", "success");
      } else {
        await axios.put(getApiUrl(`${companyId}/templates/${templateId}`), payload, { withCredentials: true });
        //swal("Success", "Template updated successfully!", "success");
      }
      history.goBack();
    } catch (error) {
      console.error('Error saving template:', error);
      setError(`Failed to save template: ${error.response?.data?.message || "Unknown error"}`);
      //swal("Error", `Failed to save template: ${error.response?.data?.message || "Unknown error"}`, "error");
    } finally {
      setIsLoading(false);
    }
  };
  // Calculate preview values
  const previewTransactions = useMemo(() => {
    return transactions.map(transaction => {
      const account = accounts.find(a => a.value === transaction.account_id);
      const debitValue = evaluateFormula(transaction.debit_formula);
      const creditValue = evaluateFormula(transaction.credit_formula);
      return {
        account_number: account?.account_number || 'Unknown',
        account_name: account?.account_name || 'Unknown Account',
        debit: debitValue,
        credit: creditValue
      };
    });
  }, [transactions, accounts, parameterValues, parameters]);
  const totalDebits = useMemo(() => {
    return previewTransactions.reduce((sum, t) => sum + (t.debit || 0), 0);
  }, [previewTransactions]);
  const totalCredits = useMemo(() => {
    return previewTransactions.reduce((sum, t) => sum + (t.credit || 0), 0);
  }, [previewTransactions]);
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;
  if (fetchLoading) {
    return (
      <>
        <style>{`
          .modern-card {
            background: white;
            border-radius: 12px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            transition: all 0.2s ease;
            margin-bottom: 1.5rem;
            overflow: hidden;
          }

          .modern-card-header {
            background: #f9fafb;
            padding: 1.25rem 1.5rem;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .modern-card-body {
            padding: 1.5rem;
          }

          .section-title {
            font-size: 1.125rem;
            font-weight: 600;
            color: #374151;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .section-subtitle {
            color: #6b7280;
            font-size: 0.875rem;
            margin: 0;
            margin-top: 0.25rem;
          }

          .modern-button {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1.5rem;
            border-radius: 8px;
            font-weight: 500;
            font-size: 0.875rem;
            border: none;
            transition: all 0.3s ease;
            cursor: pointer;
            text-decoration: none;
          }

          .modern-button.primary {
            background: var(--primary);
            color: white;
          }

          .modern-button.primary:hover {
            background: var(--primary-hover);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px var(--rgba-primary-3);
          }

          .modern-button.secondary {
            background: #f9fafb;
            color: #374151;
            border: 1px solid #d1d5db;
          }

          .modern-button.secondary:hover {
            background: #f3f4f6;
            border-color: #9ca3af;
          }

          .modern-button.outline {
            background: transparent;
            color: var(--primary);
            border: 1px solid var(--primary);
          }

          .modern-button.outline:hover {
            background: var(--rgba-primary-1);
          }

          .modern-button.danger {
            background: #fef2f2;
            color: #dc2626;
            border: 1px solid #fecaca;
          }

          .modern-button.danger:hover {
            background: #dc2626;
            color: white;
          }

          .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 4rem 2rem;
            background: white;
            border-radius: 12px;
            border: 1px solid #e5e7eb;
            margin: 2rem 0;
          }

          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 3px solid #f3f4f6;
            border-top: 3px solid var(--primary);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .loading-text {
            color: #6b7280;
            font-size: 0.875rem;
          }
        `}</style>

        <PageTitle activeMenu={"Mallar"} motherMenu={"Inställningar"} />

        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Laddar malldata...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
          margin-bottom: 1.5rem;
          overflow: hidden;
        }

        .modern-card-header {
          background: #f9fafb;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modern-card-body {
          padding: 1.5rem;
        }

        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #374151;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-subtitle {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0;
          margin-top: 0.25rem;
        }

        .modern-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.3s ease;
          cursor: pointer;
          text-decoration: none;
        }

        .modern-button.primary {
          background: var(--primary);
          color: white;
        }

        .modern-button.primary:hover {
          background: var(--primary-hover);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px var(--rgba-primary-3);
        }

        .modern-button.secondary {
          background: #f9fafb;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .modern-button.secondary:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .modern-button.outline {
          background: transparent;
          color: var(--primary);
          border: 1px solid var(--primary);
        }

        .modern-button.outline:hover {
          background: var(--rgba-primary-1);
        }

        .modern-button.danger {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .modern-button.danger:hover {
          background: #dc2626;
          color: white;
        }

        .modern-button.link {
          background: transparent;
          color: var(--primary);
          border: none;
          padding: 0.5rem;
          text-decoration: none;
        }

        .modern-button.link:hover {
          background: var(--rgba-primary-1);
          border-radius: 6px;
        }

        .modern-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          background: white;
        }

        .modern-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
        }

        .modern-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
          display: block;
        }

        .modern-table-container {
          background: #f9fafb;
          border-radius: 8px;
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1rem;
        }

        .modern-table {
          margin: 0;
          background: white;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .modern-table thead th {
          background: #f3f4f6;
          border: none;
          color: #4b5563;
          font-weight: 600;
          font-size: 0.875rem;
          padding: 0.875rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modern-table tbody td {
          border: none;
          padding: 0.875rem;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }

        .modern-table tbody tr:last-child td {
          border-bottom: none;
        }

        .status-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.75rem;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
        }

        .status-badge.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .status-badge.danger {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .parameter-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .parameter-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .parameter-title {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .parameter-description {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0;
        }

        .preview-section {
          position: sticky;
          top: 20px;
        }

        .preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .preview-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .balance-warning {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          color: #92400e;
          padding: 1rem;
          border-radius: 8px;
          margin-top: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          margin: 1.5rem -1.5rem -1.5rem -1.5rem;
        }

        .section-divider {
          height: 1px;
          background: #e5e7eb;
          margin: 2rem 0;
        }

        @media (max-width: 768px) {
          .modern-card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .preview-section {
            position: static;
            margin-top: 2rem;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .modern-button {
            justify-content: center;
          }
        }
      `}</style>

      <div className="mt-4">
        <PageTitle activeMenu={"Mallar"} motherMenu={"Inställningar"} />

        <button
          className="modern-button secondary mb-4"
          onClick={() => history.goBack()}
          disabled={isLoading}
        >
          <ArrowLeft size={16} />
          Tillbaka
        </button>

        {error && (
          <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        <div className="row">
          {/* Left Side: Template Configuration */}
          <div className="col-lg-6">
            <div className="modern-card">
              <div className="modern-card-header">
                <div>
                  <h2 className="section-title">
                    <FileText size={24} />
                    {templateId === 'new' ? 'Skapa ny mall' : 'Redigera mall'}
                  </h2>
                  <p className="section-subtitle">
                    Konfigurera mallens grundinställningar och transaktioner
                  </p>
                </div>
              </div>

              <div className="modern-card-body">
                <Form>
                  {/* Basic Template Info */}
                  <div className="mb-4">
                    <label className="modern-label">Mallnamn</label>
                    <input
                      type="text"
                      className="modern-input"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Ange mallnamn"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="modern-label">Beskrivning</label>
                    <textarea
                      className="modern-input"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Beskriv vad mallen används för"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="modern-label">Malltyp</label>
                    <select
                      className="modern-input"
                      value={templateType}
                      onChange={(e) => setTemplateType(e.target.value)}
                      required
                    >
                      <option value="Income">Inkomst</option>
                      <option value="Expense">Utgift</option>
                      <option value="Investment">Investering</option>
                      <option value="Taxes">Skatter</option>
                      <option value="Other">Annat</option>
                    </select>
                  </div>

                  <div className="section-divider"></div>

                  {/* Parameters Section */}
                  <div className="parameter-section">
                    <div className="parameter-header">
                      <Calculator size={20} />
                      <div>
                        <h3 className="parameter-title">Parametrar</h3>
                        <p className="parameter-description">
                          Definiera variabler som kan användas i transaktionsformler
                        </p>
                      </div>
                    </div>

                    <div className="modern-table-container">
                      <Table responsive className="modern-table">
                        <thead>
                          <tr>
                            <th>Parameternamn</th>
                            <th>Titel</th>
                            <th>Typ</th>
                            <th>Obligatorisk</th>
                            <th width="60"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {parameters.map((parameter, index) => (
                            <tr key={index}>
                              <td>
                                <input
                                  type="text"
                                  className="modern-input"
                                  value={parameter.parameter_name}
                                  onChange={(e) => handleParameterChange(index, 'parameter_name', e.target.value)}
                                  placeholder="t.ex. amount"
                                  style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="modern-input"
                                  value={parameter.parameter_label}
                                  onChange={(e) => handleParameterChange(index, 'parameter_label', e.target.value)}
                                  placeholder="t.ex. Belopp (SEK)"
                                  style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                                />
                              </td>
                              <td>
                                <select
                                  className="modern-input"
                                  value={parameter.parameter_type}
                                  onChange={(e) => handleParameterChange(index, 'parameter_type', e.target.value)}
                                  style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                                >
                                  <option value="Number">Nummer</option>
                                  <option value="Text">Text</option>
                                  <option value="Date">Datum</option>
                                  <option value="Boolean">Sant/Falskt</option>
                                </select>
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="checkbox"
                                  checked={parameter.required}
                                  onChange={(e) => handleParameterChange(index, 'required', e.target.checked)}
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="modern-button danger"
                                  style={{ padding: '0.5rem' }}
                                  onClick={() => handleRemoveParameter(index)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    <button
                      type="button"
                      className="modern-button outline"
                      onClick={handleAddParameter}
                    >
                      <Plus size={16} />
                      Lägg till parameter
                    </button>
                  </div>

                  {/* Transactions Section */}
                  <div className="parameter-section">
                    <div className="parameter-header">
                      <FileText size={20} />
                      <div>
                        <h3 className="parameter-title">Transaktioner</h3>
                        <p className="parameter-description">
                          Definiera kontotransaktioner med formler baserade på parametrar
                        </p>
                      </div>
                    </div>

                    <div className="modern-table-container">
                      <Table responsive className="modern-table">
                        <thead>
                          <tr>
                            <th>Konto</th>
                            <th>Debetformel</th>
                            <th>Kreditformel</th>
                            <th width="60"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((transaction, index) => (
                            <tr key={index}>
                              <td>
                                <Select
                                  options={accounts}
                                  value={accounts.find(option => option.value === transaction.account_id)}
                                  onChange={(selectedOption) => handleTransactionChange(index, 'account_id', selectedOption ? selectedOption.value : '')}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                      ...base,
                                      border: '2px solid #e5e7eb',
                                      borderRadius: '8px',
                                      fontSize: '0.8rem'
                                    })
                                  }}
                                  placeholder="Välj konto"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="modern-input"
                                  value={transaction.debit_formula || ''}
                                  onChange={(e) => handleTransactionChange(index, 'debit_formula', e.target.value)}
                                  placeholder="t.ex. amount"
                                  style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="modern-input"
                                  value={transaction.credit_formula || ''}
                                  onChange={(e) => handleTransactionChange(index, 'credit_formula', e.target.value)}
                                  placeholder="t.ex. amount * 0.25"
                                  style={{ fontSize: '0.8rem', padding: '0.5rem' }}
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="modern-button danger"
                                  style={{ padding: '0.5rem' }}
                                  onClick={() => handleRemoveTransaction(index)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    <button
                      type="button"
                      className="modern-button outline"
                      onClick={handleAddTransaction}
                    >
                      <Plus size={16} />
                      Lägg till transaktion
                    </button>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="modern-button secondary"
                      onClick={() => history.goBack()}
                      disabled={isLoading}
                    >
                      Avbryt
                    </button>
                    <button
                      type="button"
                      className="modern-button primary"
                      onClick={handleSubmit}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Sparar mall...
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          {templateId === 'new' ? 'Skapa mall' : 'Spara ändringar'}
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          </div>

          {/* Right Side: Preview */}
          <div className="col-lg-6">
            <div className="preview-section">
              {parameters.length > 0 && (
                <div className="modern-card mb-4">
                  <div className="modern-card-header">
                    <div>
                      <h3 className="section-title">
                        <Calculator size={20} />
                        Testparametrar
                      </h3>
                      <p className="section-subtitle">
                        Ange värden för att förhandsgranska resultatet
                      </p>
                    </div>
                  </div>
                  <div className="modern-card-body">
                    <div className="row">
                      {parameters.map((param, index) => (
                        <div className="col-md-6 mb-3" key={index}>
                          <label className="modern-label">
                            {param.parameter_label || param.parameter_name}
                          </label>
                          {param.parameter_type === 'Number' ? (
                            <input
                              type="number"
                              className="modern-input"
                              value={parameterValues[param.parameter_name] || 0}
                              onChange={(e) => handleParameterValueChange(param.parameter_name, Number(e.target.value))}
                            />
                          ) : param.parameter_type === 'Date' ? (
                            <input
                              type="date"
                              className="modern-input"
                              value={parameterValues[param.parameter_name] || ''}
                              onChange={(e) => handleParameterValueChange(param.parameter_name, e.target.value)}
                            />
                          ) : param.parameter_type === 'Boolean' ? (
                            <Form.Check
                              type="checkbox"
                              checked={parameterValues[param.parameter_name] || false}
                              onChange={(e) => handleParameterValueChange(param.parameter_name, e.target.checked)}
                              label="Sant/Falskt"
                            />
                          ) : (
                            <input
                              type="text"
                              className="modern-input"
                              value={parameterValues[param.parameter_name] || ''}
                              onChange={(e) => handleParameterValueChange(param.parameter_name, e.target.value)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="modern-card">
                <div className="modern-card-header">
                  <div className="preview-header">
                    <h3 className="preview-title">
                      <Eye size={20} />
                      Förhandsvisning
                    </h3>
                    {transactions.length > 0 && (
                      <div className={`status-badge ${isBalanced ? 'success' : 'danger'}`}>
                        {isBalanced ? (
                          <>
                            <CheckCircle size={14} />
                            Balanserad
                          </>
                        ) : (
                          <>
                            <AlertTriangle size={14} />
                            Obalanserad
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modern-card-body">
                  {transactions.length === 0 ? (
                    <div className="text-center py-4">
                      <FileText size={48} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
                      <h4 style={{ color: '#4b5563', marginBottom: '0.5rem' }}>Inga transaktioner</h4>
                      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                        Lägg till en transaktion för att se förhandsvisning
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-3">
                        <strong>Mallnamn:</strong> {templateName || 'Namnlös mall'}
                      </div>
                      {description && (
                        <div className="mb-3">
                          <strong>Beskrivning:</strong> {description}
                        </div>
                      )}

                      <div className="modern-table-container">
                        <Table responsive className="modern-table">
                          <thead>
                            <tr>
                              <th>Konto</th>
                              <th className="text-end">Debet</th>
                              <th className="text-end">Kredit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewTransactions.map((transaction, index) => (
                              <tr key={index}>
                                <td>
                                  <div>
                                    <strong>{transaction.account_number}</strong>
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                      {transaction.account_name}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-end">
                                  {transaction.debit ?
                                    new Intl.NumberFormat('sv-SE', {
                                      minimumFractionDigits: 2,
                                      style: 'currency',
                                      currency: 'SEK'
                                    }).format(transaction.debit) :
                                    '-'}
                                </td>
                                <td className="text-end">
                                  {transaction.credit ?
                                    new Intl.NumberFormat('sv-SE', {
                                      minimumFractionDigits: 2,
                                      style: 'currency',
                                      currency: 'SEK'
                                    }).format(transaction.credit) :
                                    '-'}
                                </td>
                              </tr>
                            ))}
                            <tr style={{ fontWeight: 'bold', borderTop: '2px solid #e5e7eb' }}>
                              <td>Totalt</td>
                              <td className="text-end">
                                {new Intl.NumberFormat('sv-SE', {
                                  minimumFractionDigits: 2,
                                  style: 'currency',
                                  currency: 'SEK'
                                }).format(totalDebits)}
                              </td>
                              <td className="text-end">
                                {new Intl.NumberFormat('sv-SE', {
                                  minimumFractionDigits: 2,
                                  style: 'currency',
                                  currency: 'SEK'
                                }).format(totalCredits)}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>

                      {!isBalanced && (
                        <div className="balance-warning">
                          <AlertTriangle size={16} />
                          <div>
                            <strong>Mallen är inte balanserad!</strong>
                            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              Debet och kredit måste vara lika stora för att mallen ska vara korrekt.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TemplateEditor;







// import React, { useState, useEffect, useMemo } from 'react';
// import { Button, Form, Table, Card, Spinner, Alert, Badge } from 'react-bootstrap';
// import { useHistory, useParams } from 'react-router-dom';
// import axios from '../../../services/AxiosInstance';
// import Select from 'react-select';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faTrash, faArrowLeft, faInfoCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
// import { getApiUrl } from '../../../utils/ApiUtils';
// import swal from 'sweetalert';
// import PageTitle from '../../layouts/PageTitle';

// function TemplateEditor() {
//   const history = useHistory();
//   const { companyId, templateId } = useParams();

//   const [templateName, setTemplateName] = useState('');
//   const [description, setDescription] = useState('');
//   const [templateType, setTemplateType] = useState('Income');
//   const [transactions, setTransactions] = useState([{ account_id: '', debit_formula: '', credit_formula: '', is_user_defined: false }]);
//   const [parameters, setParameters] = useState([]);
//   const [accounts, setAccounts] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [fetchLoading, setFetchLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [parameterValues, setParameterValues] = useState({});

//   useEffect(() => {
//     const initializeData = async () => {
//       try {
//         setFetchLoading(true);
//         await fetchAccounts();

//         if (templateId !== 'new') {
//           await fetchTemplateDetails(templateId);
//         }
//       } catch (error) {
//         console.error('Error initializing data:', error);
//         setError('Failed to load data. Please try again.');
//       } finally {
//         setFetchLoading(false);
//       }
//     };

//     initializeData();
//   }, [templateId, companyId]);

//   // Update parameter values when parameters change
//   useEffect(() => {
//     const newValues = {};
//     parameters.forEach(param => {
//       // Set appropriate default sample values based on parameter type
//       if (param.parameter_name) {
//         switch (param.parameter_type) {
//           case 'Number':
//             newValues[param.parameter_name] = 1000;
//             break;
//           case 'Date':
//             newValues[param.parameter_name] = new Date().toISOString().split('T')[0];
//             break;
//           case 'Text':
//             newValues[param.parameter_name] = 'Sample Text';
//             break;
//           case 'Boolean':
//             newValues[param.parameter_name] = true;
//             break;
//           default:
//             newValues[param.parameter_name] = 1000;
//         }
//       }
//     });

//     setParameterValues(prev => ({
//       ...prev,
//       ...newValues
//     }));
//   }, [parameters]);

//   const fetchAccounts = async () => {
//     try {
//       const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true });
//       const options = result.data.map(account => ({
//         value: account.account_id,
//         label: `${account.account_number} - ${account.account_name}`,
//         account_number: account.account_number,
//         account_name: account.account_name
//       }));
//       setAccounts(options);
//     } catch (error) {
//       console.error('Error fetching accounts:', error);
//       throw error;
//     }
//   };

//   const fetchTemplateDetails = async (id) => {
//     try {
//       const response = await axios.get(getApiUrl(`${companyId}/templates/${id}`), { withCredentials: true });
//       const template = response.data.template;

//       setTemplateName(template.template_name || '');
//       setDescription(template.description || '');
//       setTemplateType(template.template_type || 'Income');

//       // Ensure transactions has at least one entry
//       if (template.transactions && template.transactions.length > 0) {
//         setTransactions(template.transactions);
//       } else {
//         setTransactions([{ account_id: '', debit_formula: '', credit_formula: '', is_user_defined: false }]);
//       }

//       // Set parameters if they exist
//       if (template.parameters && template.parameters.length > 0) {
//         setParameters(template.parameters);
//       }
//     } catch (error) {
//       console.error('Error fetching template:', error);
//       throw error;
//     }
//   };

//   const handleTransactionChange = (index, field, value) => {
//     const newTransactions = [...transactions];
//     newTransactions[index][field] = value;
//     setTransactions(newTransactions);
//   };

//   const handleAddTransaction = () => {
//     setTransactions([...transactions, { account_id: '', debit_formula: '', credit_formula: '', is_user_defined: false }]);
//   };

//   const handleRemoveTransaction = (index) => {
//     if (transactions.length > 1) {
//       setTransactions(transactions.filter((_, i) => i !== index));
//     } else {
//       swal('Cannot Remove', 'You need at least one transaction in the template.', 'warning');
//     }
//   };

//   const handleAddParameter = () => {
//     setParameters([...parameters, { parameter_name: '', parameter_label: '', parameter_type: 'Number', required: true }]);
//   };

//   const handleParameterChange = (index, field, value) => {
//     const newParameters = [...parameters];
//     newParameters[index][field] = value;

//     // If changing parameter_name, update it to be a valid variable name
//     if (field === 'parameter_name') {
//       const validName = value
//         .toLowerCase()
//         .replace(/[^a-z0-9_]/gi, '_')
//         .replace(/^[0-9]/, '_');

//       newParameters[index].parameter_name = validName;
//     }

//     setParameters(newParameters);
//   };

//   const handleRemoveParameter = (index) => {
//     setParameters(parameters.filter((_, i) => i !== index));
//   };

//   const handleParameterValueChange = (paramName, value) => {
//     setParameterValues({
//       ...parameterValues,
//       [paramName]: value
//     });
//   };

//   const evaluateFormula = (formula) => {
//     if (!formula) return 0;

//     try {
//       // Replace parameters with their values
//       let evaluatableFormula = formula;

//       parameters.forEach(param => {
//         const paramName = param.parameter_name;
//         if (paramName) {
//           const regex = new RegExp(paramName, 'g');
//           evaluatableFormula = evaluatableFormula.replace(regex, parameterValues[paramName] || 0);
//         }
//       });

//       // Evaluate the formula
//       // eslint-disable-next-line no-eval
//       const result = eval(evaluatableFormula);
//       return isNaN(result) ? 0 : result;
//     } catch (error) {
//       console.error('Error evaluating formula:', error);
//       return 0;
//     }
//   };

//   const validateTemplate = () => {
//     // Basic validation
//     if (!templateName.trim()) {
//       swal("Validation Error", "Template Name is required!", "error");
//       return false;
//     }

//     // Validate transactions
//     const invalidTransactions = transactions.filter(
//       t => !t.account_id || (!t.debit_formula && !t.credit_formula)
//     );

//     if (invalidTransactions.length > 0) {
//       swal("Validation Error", "All transactions must have an account and at least one formula (debit or credit).", "error");
//       return false;
//     }

//     // Validate parameters have names and labels
//     const invalidParameters = parameters.filter(
//       p => !p.parameter_name || !p.parameter_label
//     );

//     if (invalidParameters.length > 0) {
//       swal("Validation Error", "All parameters must have both a name and label.", "error");
//       return false;
//     }

//     // Check for duplicate parameter names
//     const paramNames = parameters.map(p => p.parameter_name);
//     if (new Set(paramNames).size !== paramNames.length) {
//       swal("Validation Error", "Parameter names must be unique.", "error");
//       return false;
//     }

//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateTemplate()) {
//       return;
//     }

//     const payload = {
//       template_name: templateName,
//       description,
//       template_type: templateType,
//       transactions,
//       parameters
//     };

//     try {
//       setIsLoading(true);
//       setError(null);

//       if (templateId === 'new') {
//         await axios.post(getApiUrl(`${companyId}/templates`), payload, { withCredentials: true });
//         swal("Success", "Template created successfully!", "success");
//       } else {
//         await axios.put(getApiUrl(`${companyId}/templates/${templateId}`), payload, { withCredentials: true });
//         swal("Success", "Template updated successfully!", "success");
//       }

//       history.goBack();
//     } catch (error) {
//       console.error('Error saving template:', error);
//       setError(`Failed to save template: ${error.response?.data?.message || "Unknown error"}`);
//       swal("Error", `Failed to save template: ${error.response?.data?.message || "Unknown error"}`, "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Calculate preview values
//   const previewTransactions = useMemo(() => {
//     return transactions.map(transaction => {
//       const account = accounts.find(a => a.value === transaction.account_id);
//       const debitValue = evaluateFormula(transaction.debit_formula);
//       const creditValue = evaluateFormula(transaction.credit_formula);

//       return {
//         account_number: account?.account_number || 'Unknown',
//         account_name: account?.account_name || 'Unknown Account',
//         debit: debitValue,
//         credit: creditValue
//       };
//     });
//   }, [transactions, accounts, parameterValues, parameters]);

//   const totalDebits = useMemo(() => {
//     return previewTransactions.reduce((sum, t) => sum + (t.debit || 0), 0);
//   }, [previewTransactions]);

//   const totalCredits = useMemo(() => {
//     return previewTransactions.reduce((sum, t) => sum + (t.credit || 0), 0);
//   }, [previewTransactions]);

//   const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

//   if (fetchLoading) {
//     return (
//       <div className="container mt-4 text-center">
//         <Spinner animation="border" role="status">
//           <span className="visually-hidden">Loading...</span>
//         </Spinner>
//         <p className="mt-2">Loading template data...</p>
//       </div>
//     );
//   }

//   return (

//     <div className="mt-4">

//       <PageTitle activeMenu={"Mallar"} motherMenu={"Inställningar"} />

//       <Button variant="secondary" onClick={() => history.goBack()} disabled={isLoading} className="mb-3">
//         <FontAwesomeIcon icon={faArrowLeft} /> Tillbaka
//       </Button>

//       <h2 className="mb-4">{templateId === 'new' ? 'Skapa ny mall' : 'Redigera mall'}</h2>

//       {error && (
//         <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
//           {error}
//         </Alert>
//       )}

//       <div className="row">
//         {/* Left Side: Template Configuration */}
//         <div className="col-md-6">
//           <Card className="mb-4">
//             <Card.Body>
//               <Form>
//                 <Form.Group className="mb-3">
//                   <Form.Label>Namn</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={templateName}
//                     onChange={(e) => setTemplateName(e.target.value)}
//                     required
//                   />
//                 </Form.Group>

//                 <Form.Group className="mb-3">
//                   <Form.Label>Beskrivning</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={2}
//                     value={description}
//                     onChange={(e) => setDescription(e.target.value)}
//                   />
//                 </Form.Group>

//                 <Form.Group className="mb-4">
//                   <Form.Label>Typ</Form.Label>
//                   <Form.Select
//                     value={templateType}
//                     onChange={(e) => setTemplateType(e.target.value)}
//                     required
//                   >
//                     <option value="Income">Inkomst</option>
//                     <option value="Expense">Utgift</option>
//                     <option value="Investment">Investering</option>
//                     <option value="Taxes">Skatter</option>
//                     <option value="Other">Annat</option>
//                   </Form.Select>
//                 </Form.Group>

//                 <h5 className="mb-3">Parameterar</h5>
//                 <div className="small text-muted mb-3">
//                   Definera parametrar som kan användas i transaktioner.
//                 </div>

//                 <Table responsive className="mb-2">
//                   <thead>
//                     <tr>
//                       <th>Parameternamn</th>
//                       <th>Titel</th>
//                       <th>Typ</th>
//                       <th>Nödvändig</th>
//                       <th></th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {parameters.map((parameter, index) => (
//                       <tr key={index}>
//                         <td>
//                           <Form.Control
//                             type="text"
//                             value={parameter.parameter_name}
//                             onChange={(e) => handleParameterChange(index, 'parameter_name', e.target.value)}
//                             placeholder="e.g. amount"
//                             size="sm"
//                           />
//                         </td>
//                         <td>
//                           <Form.Control
//                             type="text"
//                             value={parameter.parameter_label}
//                             onChange={(e) => handleParameterChange(index, 'parameter_label', e.target.value)}
//                             placeholder="e.g. Amount (SEK)"
//                             size="sm"
//                           />
//                         </td>
//                         <td>
//                           <Form.Select
//                             value={parameter.parameter_type}
//                             onChange={(e) => handleParameterChange(index, 'parameter_type', e.target.value)}
//                             size="sm"
//                           >
//                             <option value="Number">Number</option>
//                             <option value="Text">Text</option>
//                             <option value="Date">Date</option>
//                             <option value="Boolean">Boolean</option>
//                           </Form.Select>
//                         </td>
//                         <td>
//                           <Form.Check
//                             type="checkbox"
//                             checked={parameter.required}
//                             onChange={(e) => handleParameterChange(index, 'required', e.target.checked)}
//                           />
//                         </td>
//                         <td>
//                           <Button variant="link" onClick={() => handleRemoveParameter(index)}>
//                             <FontAwesomeIcon icon={faTrash} />
//                           </Button>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </Table>
//                 <Button variant="link" onClick={handleAddParameter} className="mb-4">
//                   <FontAwesomeIcon icon={faPlus} /> Lägg till parameter
//                 </Button>

//                 <h5 className="mb-3 mt-4">Transasktioner</h5>
//                 <div className="small text-muted mb-3">
//                   Definiera transaktioner som skall med i verifikatet. Använd parametrar för dynamiska värden.
//                 </div>
//                 <Table responsive className="mb-2">
//                   <thead>
//                     <tr>
//                       <th>Konto</th>
//                       <th>Debetformell</th>
//                       <th>Kreditformell</th>
//                       <th></th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {transactions.map((transaction, index) => (
//                       <tr key={index}>
//                         <td>
//                           <Select
//                             options={accounts}
//                             value={accounts.find(option => option.value === transaction.account_id)}
//                             onChange={(selectedOption) => handleTransactionChange(index, 'account_id', selectedOption ? selectedOption.value : '')}
//                             menuPortalTarget={document.body}
//                             styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
//                             placeholder="Välj ett konto"
//                           />
//                         </td>
//                         <td>
//                           <Form.Control
//                             type="text"
//                             value={transaction.debit_formula || ''}
//                             onChange={(e) => handleTransactionChange(index, 'debit_formula', e.target.value)}
//                             placeholder="t.ex. amount"
//                           />
//                         </td>
//                         <td>
//                           <Form.Control
//                             type="text"
//                             value={transaction.credit_formula || ''}
//                             onChange={(e) => handleTransactionChange(index, 'credit_formula', e.target.value)}
//                             placeholder="t.ex amount * 0.25"
//                           />
//                         </td>
//                         <td>
//                           <Button variant="link" onClick={() => handleRemoveTransaction(index)}>
//                             <FontAwesomeIcon icon={faTrash} />
//                           </Button>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </Table>
//                 <Button variant="link" onClick={handleAddTransaction} className="mb-4">
//                   <FontAwesomeIcon icon={faPlus} /> Add Transaction
//                 </Button>

//                 <div className="d-flex justify-content-end mt-4">
//                   <Button variant="outline-secondary" onClick={() => history.goBack()} disabled={isLoading} className="me-2">
//                     Avbryt
//                   </Button>
//                   <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
//                     {isLoading ? (
//                       <><Spinner animation="border" size="sm" className="me-2" /> {'Sparar mall...'}</>
//                     ) : (
//                       templateId === 'new' ? 'Skapa mall' : 'Spara ändringar'
//                     )}
//                   </Button>
//                 </div>
//               </Form>
//             </Card.Body>
//           </Card>
//         </div>

//         {/* Right Side: Preview */}
//         <div className="col-md-6">
//           <div className="sticky-top" style={{ top: '20px' }}>
//             {parameters.length > 0 && (
//               <Card className="mb-4">
//                 <Card.Header>
//                   <h5 className="mb-0">Exempelvärden för parametrar</h5>
//                 </Card.Header>
//                 <Card.Body>
//                   <div className="row">
//                     {parameters.map((param, index) => (
//                       <div className="col-md-6" key={index}>
//                         <Form.Group className="mb-3">
//                           <Form.Label>{param.parameter_label || param.parameter_name}</Form.Label>
//                           {param.parameter_type === 'Number' ? (
//                             <Form.Control
//                               type="number"
//                               value={parameterValues[param.parameter_name] || 0}
//                               onChange={(e) => handleParameterValueChange(param.parameter_name, Number(e.target.value))}
//                             />
//                           ) : param.parameter_type === 'Date' ? (
//                             <Form.Control
//                               type="date"
//                               value={parameterValues[param.parameter_name] || ''}
//                               onChange={(e) => handleParameterValueChange(param.parameter_name, e.target.value)}
//                             />
//                           ) : param.parameter_type === 'Boolean' ? (
//                             <Form.Check
//                               type="checkbox"
//                               checked={parameterValues[param.parameter_name] || false}
//                               onChange={(e) => handleParameterValueChange(param.parameter_name, e.target.checked)}
//                               label="True/False"
//                             />
//                           ) : (
//                             <Form.Control
//                               type="text"
//                               value={parameterValues[param.parameter_name] || ''}
//                               onChange={(e) => handleParameterValueChange(param.parameter_name, e.target.value)}
//                             />
//                           )}
//                         </Form.Group>
//                       </div>
//                     ))}
//                   </div>
//                 </Card.Body>
//               </Card>
//             )}

//             <Card>
//               <Card.Header className="d-flex justify-content-between align-items-center">
//                 <h5 className="mb-0">Exempel på verifikat</h5>
//                 {transactions.length > 0 && (
//                   <Badge bg={isBalanced ? 'success' : 'danger'}>
//                     {isBalanced ? 'Balanced' : 'Unbalanced'}
//                   </Badge>
//                 )}
//               </Card.Header>
//               <Card.Body>
//                 {transactions.length === 0 ? (
//                   <p className="text-muted">Lägg till en transaktion för att se ett exempel.</p>
//                 ) : (
//                   <div>
//                     <div className="mb-3">
//                       <strong>Titel:</strong> {templateName || 'Ej Namngiven'}
//                     </div>
//                     {description && (
//                       <div className="mb-3">
//                         <strong>Beskrivning:</strong> {description}
//                       </div>
//                     )}

//                     <Table responsive bordered className="mt-3">
//                       <thead>
//                         <tr>
//                           <th>Konto</th>
//                           <th className="text-end">Debet</th>
//                           <th className="text-end">Kredit</th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {previewTransactions.map((transaction, index) => (
//                           <tr key={index}>
//                             <td>{transaction.account_number} - {transaction.account_name}</td>
//                             <td className="text-end">
//                               {transaction.debit ?
//                                 new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(transaction.debit) :
//                                 '-'}
//                             </td>
//                             <td className="text-end">
//                               {transaction.credit ?
//                                 new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(transaction.credit) :
//                                 '-'}
//                             </td>
//                           </tr>
//                         ))}
//                         <tr className="fw-bold">
//                           <td>Totalt</td>
//                           <td className="text-end">
//                             {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(totalDebits)}
//                           </td>
//                           <td className="text-end">
//                             {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(totalCredits)}
//                           </td>
//                         </tr>
//                       </tbody>
//                     </Table>

//                     {!isBalanced && (
//                       <Alert variant="warning" className="mt-3">
//                         <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
//                         This template is not balanced. The total debits should equal the total credits.
//                       </Alert>
//                     )}
//                   </div>
//                 )}
//               </Card.Body>
//             </Card>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default TemplateEditor;